import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Trash2, Film, Clock, Plus } from "lucide-react";
import { api, mediaUrl } from "../lib/api";

export default function ProjectsPage() {
  const [projects, setProjects] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/projects");
      setProjects(data);
    } catch (e) {
      setProjects([]);
    }
  };

  useEffect(() => { load(); }, []);

  const handleDelete = async (e, pid) => {
    e.preventDefault();
    try {
      await api.delete(`/projects/${pid}`);
      toast.success("Projet supprimé");
      load();
    } catch {
      toast.error("Échec de la suppression");
    }
  };

  if (!projects)
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 className="w-8 h-8 text-amber-500 animate-spin" />
      </div>
    );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight text-white mb-8">Mes Court-Métrages</h1>

      {projects.length === 0 ? (
        <div className="text-center py-20 text-slate-400" data-testid="empty-projects-state">
          <Film className="w-12 h-12 mx-auto mb-4 text-slate-600" />
          <p className="mb-6">Aucun film pour le moment.</p>
          <Link
            to="/"
            data-testid="empty-new-film-link"
            className="inline-flex items-center gap-2 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-950 font-bold px-6 py-3 rounded-xl hover:from-amber-400 hover:to-amber-500"
          >
            <Plus className="w-4 h-4" /> Créer mon premier film
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {projects.map((p) => (
            <Link
              key={p.id}
              to={`/film/${p.id}`}
              data-testid="project-card-item"
              className="group bg-[#12151B]/80 backdrop-blur-xl border border-white/10 hover:border-amber-500/30 rounded-2xl overflow-hidden transition-all duration-300"
            >
              <div className="aspect-video bg-[#090B0E] relative overflow-hidden">
                {p.preview_image_url ? (
                  <img src={mediaUrl(p.preview_image_url)} alt={p.title || "Film"} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Film className="w-10 h-10 text-slate-700" />
                  </div>
                )}
                <span className="absolute top-3 left-3 bg-black/60 border border-amber-500/30 text-amber-400 font-mono text-xs px-2.5 py-1 rounded-md tracking-widest backdrop-blur-sm">
                  {p.duration_min} MIN
                </span>
                {p.status === "generating" && (
                  <span className="absolute top-3 right-3 bg-black/60 text-xs text-white px-2.5 py-1 rounded-md flex items-center gap-1.5">
                    <Loader2 className="w-3 h-3 animate-spin" /> Écriture…
                  </span>
                )}
              </div>
              <div className="p-4">
                <h3 className="font-bold text-white truncate mb-1">{p.title || p.prompt}</h3>
                <div className="flex items-center justify-between">
                  <p className="text-xs text-slate-400 flex items-center gap-1.5">
                    <Clock className="w-3 h-3" />
                    {p.genre} • {p.scene_count || 0} scènes
                  </p>
                  <button
                    data-testid={`delete-project-${p.id}`}
                    onClick={(e) => handleDelete(e, p.id)}
                    className="text-slate-500 hover:text-rose-500 p-1.5 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
